import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, formatPercent } from '@companion-professional/webutils';
import { Badge } from '../Badge';
import { Eyebrow, Text } from '../Typography';

const probabilityValueVariants = cva(['font-display', 'font-normal', 'leading-display'], {
  variants: {
    size: {
      md: 'text-3xl',
      lg: 'text-5xl'
    },
    emphasized: {
      false: 'text-heading',
      true: 'text-destructive'
    }
  },
  defaultVariants: {
    size: 'lg',
    emphasized: false
  }
});

export interface ProbabilityDisplayProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof probabilityValueVariants> {
  // probability is a fraction from 0 to 1.  Values outside that range are clamped (see formatPercent).
  probability: number;
  // label names what the probability is of (e.g. "Chance of rain").  It is shown as a caption above the number and
  // is the accessible name of the group, so screen readers announce it along with the percentage.
  label: string;
  // emphasisLabel is the text of the status badge shown when `emphasized` is true (e.g. "Flagged").  Without it, the
  // emphasis is conveyed by color alone, so callers should supply it whenever they set `emphasized`.
  emphasisLabel?: string;
  // description is supporting text shown below the number.
  description?: React.ReactNode;
  // fractionDigits is the number of decimal places shown in the percentage.
  fractionDigits?: number;
}

// ProbabilityDisplay shows a probability prominently as a percentage.  Set `emphasized` to draw attention to a value the
// caller considers concerning; the component itself has no notion of what counts as high.
export const ProbabilityDisplay = React.forwardRef<HTMLDivElement, ProbabilityDisplayProps>(
  (
    {
      className,
      probability,
      label,
      emphasized = false,
      emphasisLabel,
      description,
      fractionDigits = 0,
      size,
      ...props
    },
    ref
  ) => {
    const labelId = React.useId();
    const descriptionId = React.useId();
    return (
      <div
        ref={ref}
        role="group"
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn('flex flex-col gap-1', className)}
        {...props}
      >
        <Eyebrow id={labelId} tone="muted">
          {label}
        </Eyebrow>
        <div className="flex flex-wrap items-center gap-3">
          <span className={probabilityValueVariants({ size, emphasized })}>
            {formatPercent(probability, fractionDigits)}
          </span>
          {emphasized && emphasisLabel && <Badge variant="danger">{emphasisLabel}</Badge>}
        </div>
        {description && (
          <Text id={descriptionId} tone="muted" size="sm">
            {description}
          </Text>
        )}
      </div>
    );
  }
);
ProbabilityDisplay.displayName = 'ProbabilityDisplay';

export { probabilityValueVariants };
